/**
 * manage/format — render-time formatting. Nothing here decides anything.
 *
 * TIME IS RENDERED IN THE BOOKING'S OWN ZONE, because that is where the
 * appointment happens: `schedule.timezone` is an IANA name and `Intl` resolves
 * its offset for the instant, so a DST rule change cannot move a booking that
 * was already confirmed. The viewer's local time is added ONLY when the view
 * says `show_viewer_local_time` AND the two zones actually differ — a second
 * identical line is noise, and the flag is policy, not a preference of ours.
 *
 * MONEY IS FORMATTED, NEVER CONCATENATED: `Intl.NumberFormat` with the
 * currency's own minor-unit exponent, so CHF 96 and JPY 96 both come out right.
 */
import type { ManageViewDto } from "@v0y4ge/api-contracts";

type Schedule = ManageViewDto["schedule"];

function safeFormat(
  locale: string,
  options: Intl.DateTimeFormatOptions,
  iso: string,
): string {
  try {
    return new Intl.DateTimeFormat(locale, options).format(new Date(iso));
  } catch {
    return iso;
  }
}

export function viewerTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "";
  } catch {
    return "";
  }
}

export interface TimeFormatter {
  /** "Monday, 15 March 2027, 14:00" in the booking's zone. */
  full: (iso: string, timeZone: string) => string;
  /** "15 March 2027" */
  date: (iso: string, timeZone: string) => string;
  /** "14:00" */
  time: (iso: string, timeZone: string) => string;
  /** "Mon 15 Mar, 14:00" — dense enough for a slot button. */
  slot: (iso: string, timeZone: string) => string;
  /** "Mon 15 Mar" — the day heading of a slot group. */
  day: (iso: string, timeZone: string) => string;
  /** Minutes between two instants, for the duration line. */
  minutesBetween: (startIso: string, endIso: string) => number;
  money: (amount: number, currency: string | null) => string;
}

export function timeFormatter(locale: string): TimeFormatter {
  return {
    full: (iso, timeZone) =>
      safeFormat(locale, {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
        hour: "2-digit", minute: "2-digit", timeZone,
      }, iso),
    date: (iso, timeZone) =>
      safeFormat(locale, { day: "numeric", month: "long", year: "numeric", timeZone }, iso),
    time: (iso, timeZone) =>
      safeFormat(locale, { hour: "2-digit", minute: "2-digit", timeZone }, iso),
    slot: (iso, timeZone) =>
      safeFormat(locale, {
        weekday: "short", day: "numeric", month: "short",
        hour: "2-digit", minute: "2-digit", timeZone,
      }, iso),
    day: (iso, timeZone) =>
      safeFormat(locale, { weekday: "short", day: "numeric", month: "short", timeZone }, iso),
    minutesBetween: (startIso, endIso) => {
      const ms = Date.parse(endIso) - Date.parse(startIso);
      return Number.isFinite(ms) ? Math.max(0, Math.round(ms / 60000)) : 0;
    },
    money: (amount, currency) => {
      if (!currency) {
        try {
          return new Intl.NumberFormat(locale).format(amount);
        } catch {
          return String(amount);
        }
      }
      try {
        return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amount);
      } catch {
        return `${currency} ${amount}`;
      }
    },
  };
}

/**
 * Should the viewer's own local time be shown as a second line? Only when the
 * policy allows it and the zones differ — comparing the resolved IANA names,
 * not the offsets, because two zones can share an offset today and not in March.
 */
export function showsViewerTime(schedule: Schedule): boolean {
  if (!schedule.show_viewer_local_time) return false;
  const viewer = viewerTimeZone();
  return Boolean(viewer) && viewer !== schedule.timezone;
}

/** "4:32" — the remaining life of a reschedule hold, for the countdown line. */
export function countdown(expiresAtIso: string, nowMs: number): string {
  const remaining = Math.max(0, Date.parse(expiresAtIso) - nowMs);
  const totalSeconds = Math.floor(remaining / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}
